// The texts of the Pro statistics widgets (VisionPlatform specs/pro-statistics PST-12 to PST-23, P-264). The fixtures'
// expected texts are produced by these rules; numbers follow WFX-06 (format.ts) and dates the organisation's time zone.
import type { PointState, StatisticsPeriod } from "./generated/widget-fixture";
import { formatValue } from "./format";

type T = (key: string, options?: Record<string, unknown>) => string;

/** A local calendar date in the organisation's time zone. */
export interface LocalDate {
  year: number;
  month: number;
  day: number;
}

export function localDate(ms: number, tz?: string): LocalDate {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(new Date(ms));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { year: get("year"), month: get("month"), day: get("day") };
}

/** The ISO week (Monday first, week 1 holds the year's first Thursday) and the year it belongs to. */
export function isoWeek({ year, month, day }: LocalDate): { week: number; year: number } {
  const d = new Date(Date.UTC(year, month - 1, day));
  const weekday = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - weekday);
  const first = Date.UTC(d.getUTCFullYear(), 0, 1);
  return { week: Math.ceil(((d.getTime() - first) / 86_400_000 + 1) / 7), year: d.getUTCFullYear() };
}

/** "3 March", with the year when it is not `year`. */
export function dayMonth(d: LocalDate, t: T, year?: number): string {
  const dm = `${d.day} ${t(`month.${d.month}`)}`;
  return year === undefined || d.year === year ? dm : `${dm} ${d.year}`;
}

/** The days of a window [from, to): "1 to 23 October 2025", "28 September to 4 October 2025". */
export function dayRange(from: string, to: string, t: T, tz?: string): string {
  const a = localDate(Date.parse(from), tz);
  // The end is exclusive: a window ending at midnight ends on the day before.
  const b = localDate(Date.parse(to) - 1, tz);
  const full = (d: LocalDate) => `${d.day} ${t(`month.${d.month}`)} ${d.year}`;
  if (a.year === b.year && a.month === b.month && a.day === b.day) return full(b);
  const start = a.year !== b.year ? full(a) : a.month !== b.month ? `${a.day} ${t(`month.${a.month}`)}` : `${a.day}`;
  return t("statistics.days", { from: start, to: full(b) });
}

/** The name of a whole period that starts at `from`: "October 2025", "Week 43, 2025", "2025". */
export function periodName(period: StatisticsPeriod["period"], from: LocalDate, t: T): string {
  if (period === "month") return t("statistics.periodMonth", { month: t(`month.${from.month}`), year: from.year });
  if (period === "week") {
    const w = isoWeek(from);
    return t("statistics.periodWeek", { count: w.week, year: w.year });
  }
  if (period === "year") return String(from.year);
  return `${from.day} ${t(`month.${from.month}`)} ${from.year}`;
}

/** Readings on fewer than 90 % of the days (hours for today) make a side incomplete (PST-23). */
export function incomplete(c: StatisticsPeriod["coverage"]): boolean {
  return c.days > 0 && c.days_with_data < 0.9 * c.days;
}

export function incompleteText(period: StatisticsPeriod["period"], c: StatisticsPeriod["coverage"], t: T): string {
  return t(period === "today" ? "statistics.incompleteHours" : "statistics.incomplete", {
    count: c.days_with_data,
    days: c.days,
  });
}

/**
 * The comparison in words (PST-22, PST-23): "It rained 12.0 mm (41 %) more than 1 to 23 October 2025", "1.2 °C
 * warmer than …", "About the same as …", or "No data for October 2025" when last year has no readings.
 */
export function compareSentence(
  per: StatisticsPeriod,
  point: Pick<PointState, "unit" | "decimals" | "trait">,
  locale: string,
  t: T,
  tz?: string,
): string {
  const ly = per.last_year;
  if (!ly || !per.difference) {
    const from = localDate(Date.parse(per.from), tz);
    const lastYear = ly ? localDate(Date.parse(ly.from), tz) : { ...from, year: from.year - 1 };
    return t("statistics.noData", { period: periodName(per.period, lastYear, t) });
  }
  const period = dayRange(ly.from, ly.to, t, tz);
  const { value, percent } = per.difference;
  if (Math.round(Math.abs(value) * 10 ** point.decimals) === 0) return t("statistics.same", { period });
  let difference = formatValue(Math.abs(value), point.unit, point.decimals, locale);
  if (per.aggregate === "sum" && percent !== null && percent !== undefined) {
    difference = t("statistics.withPercent", { difference, percent: formatValue(Math.abs(percent), "%", 0, locale) });
  }
  if (incomplete(per.coverage) || incomplete(ly.coverage)) difference = t("statistics.about", { difference });
  const up = value > 0;
  const key =
    per.aggregate === "avg"
      ? point.trait === "temperature"
        ? up
          ? "warmer"
          : "colder"
        : up
          ? "higher"
          : "lower"
      : point.trait === "rain"
        ? up
          ? "rainMore"
          : "rainLess"
        : up
          ? "more"
          : "less";
  return t(`statistics.${key}`, { difference, period });
}
